import React, { useRef, useState } from "react";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import apiAxios from "../../../services/apiAxios";
import { extentions, foodToggleValues } from "../../../utils/constants";
import Button from "../../common/Button";
import UploadFile from "../../common/icons/UploadFile";
import Camera from "../../common/icons/Camera";
import { searchCustomerByNumber } from "../../../redux/Thunk/AdminAccount.js/AccountThunk";
import SparklesIcon from "./SparklesIcon"; // We'll create this simple SVG

const calculateAIProductMetrics = (p, dbProduct) => {
  const type = dbProduct?.type || "";
  const uom = Number(dbProduct?.uom || 1) || 1;
  const isCustom = dbProduct?.isCustom || (dbProduct?.IID && String(dbProduct.IID).startsWith("CP"));
  const isCalcType = extentions[type]?.calc !== false;

  const mode = p.dosageMode || "REGULAR";
  let computedRxDays = Number(p.rxDays) || 0;
  let computedRxUnits = 0;
  let computedOrderQty = 0;

  if (mode === "SOS") {
    computedRxDays = 1;
    computedRxUnits = 1;
    computedOrderQty = isCustom ? 0 : 1;
  } else if (mode === "NA") {
    computedRxDays = 0;
    computedRxUnits = 0;
    computedOrderQty = isCustom ? 0 : 1;
  } else if (mode === "PERIODIC") {
    const freq = p.periodicFrequency || "Once a week";
    const instanceDose = Number(p.morningTime) || 1;
    let intervalDays = 7;
    if (freq === "Alternate Days") intervalDays = 2;
    else if (freq === "Twice a week") intervalDays = 3.5;
    else if (freq === "Every 3 days") intervalDays = 3;
    else if (freq === "Every 5 days") intervalDays = 5;
    else if (freq === "Every 15 days") intervalDays = 15;
    else if (freq === "Once a month") intervalDays = 30;
    else if (freq === "Single Dose" || freq === "Single Dose / Stat") intervalDays = 0;

    let daysForCalculation = computedRxDays;
    if (freq === "Once a week" || freq === "Twice a week") {
      if (computedRxDays === 30) daysForCalculation = 28;
      else if (computedRxDays === 60) daysForCalculation = 56;
      else if (computedRxDays === 90) daysForCalculation = 84;
      else if (computedRxDays === 180) daysForCalculation = 168;
      else if (computedRxDays === 270) daysForCalculation = 252;
      else if (computedRxDays === 365) daysForCalculation = 364;
    }

    const totalDoses = intervalDays > 0 ? Math.ceil((daysForCalculation || 1) / intervalDays) : 1;
    computedRxUnits = totalDoses * instanceDose;
    if (isCustom) {
      computedOrderQty = 0;
    } else if (!isCalcType || uom <= 0) {
      computedOrderQty = 1;
    } else {
      computedOrderQty = Math.ceil(computedRxUnits / uom) || 1;
    }
  } else if (mode === "REDUCING") {
    const steps = Array.isArray(p.reducingDoses) ? p.reducingDoses : [];
    let totalDays = 0;
    let totalUnits = 0;
    steps.forEach((step) => {
      const stepDays = Number(step.rxDays) || 0;
      const dailyDose = (Number(step.morningTime) || 0) + (Number(step.midDay) || 0) + (Number(step.eveningTime) || 0) + (Number(step.night) || 0);
      totalDays += stepDays;
      totalUnits += dailyDose * stepDays;
    });
    computedRxDays = totalDays;
    computedRxUnits = totalUnits;
    if (isCustom) {
      computedOrderQty = 0;
    } else if (!isCalcType || uom <= 0) {
      computedOrderQty = 1;
    } else {
      computedOrderQty = Math.ceil(totalUnits / uom) || 1;
    }
  } else {
    // REGULAR
    const dailyDose = (Number(p.morningTime) || 0) + (Number(p.midDay) || 0) + (Number(p.eveningTime) || 0) + (Number(p.night) || 0);
    computedRxUnits = computedRxDays * dailyDose;
    if (isCustom) {
      computedOrderQty = 0;
    } else if (!isCalcType || uom <= 0 || dailyDose === 0) {
      computedOrderQty = 1;
    } else {
      computedOrderQty = Math.ceil(computedRxUnits / uom) || 1;
    }
  }

  const orderUnits = isCustom ? 0 : computedOrderQty * uom;
  return { rxDays: computedRxDays, rxUnits: computedRxUnits, orderQty: computedOrderQty, orderUnits };
};

const AIPrescriptionReader = ({
  handleUpload,
  setFieldValue,
  setValues,
  values,
  setBillingData,
  onSuccessSwitchTab
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [progressLog, setProgressLog] = useState([]);
  const [cameraOn, setCameraOn] = useState(false);
  const [imageData, setImageData] = useState(null);

  const videoRef = useRef(null);
  const dispatch = useDispatch();
  const storeID = useSelector((state) => state.doctor.doctorDetails?.doctor?.SID);
  const SID = useSelector((state) => state.store.storeDetail?.store?.SID);

  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);

  const startCamera = async (silentFallback = false) => {
    setCameraOn(true); // Mount video element immediately
    try {
      const stream = await navigator?.mediaDevices?.getUserMedia({
        video: { facingMode: "environment" },
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (error) {
      setCameraOn(false); // Revert if failed
      if (!silentFallback) {
        toast.error("Camera not detected or permission denied.");
      }
      console.error("Error accessing camera:", error);
    }
  };

  // Auto-trigger camera when the modal opens
  React.useEffect(() => {
    if (isOpen && !loading && !imageData && !cameraOn) {
      startCamera(true);
    }
  }, [isOpen, cameraOn, imageData, loading]);

  const stopCamera = () => {
    if (videoRef?.current && videoRef?.current?.srcObject) {
      const stream = videoRef?.current?.srcObject;
      const tracks = stream?.getTracks();
      tracks?.forEach((track) => track?.stop());
      videoRef.current.srcObject = null;
    }
    setCameraOn(false);
  };

  const capturePhoto = () => {
    const canvas = canvasRef?.current;
    const video = videoRef?.current;
    if (canvas && video) {
      const context = canvas.getContext("2d");
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
      const dataUrl = canvas.toDataURL("image/png");
      setImageData(dataUrl);
      stopCamera();
    }
  };

  const processImageBlob = async (blob) => {
    setLoading(true);
    setProgressLog([{ msg: "Received Prescription", status: "success" }, { msg: "Uploading securely to cloud...", status: "loading" }]);
    try {
      // 1. Upload to S3 (this handles the persistent record)
      const filePath = await handleUpload(blob, "aio-uploads");
      if (!filePath) {
        throw new Error("Failed to upload document");
      }
      // Explicitly set the file path in Formik so it skips normal generation logic
      setFieldValue("filePath", filePath);
      setFieldValue("isAIGenerated", true); // Flag to skip XID generation

      setProgressLog(prev => {
        const next = [...prev];
        next[next.length - 1].status = "success";
        return [...next, { msg: "Running intelligence extraction models...", status: "loading" }];
      });

      // 2. Read with AI
      const formData = new FormData();
      formData.append("image", blob);

      const aiResponse = await apiAxios.post("api/ai/read-prescription", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      const aiData = aiResponse.data;

      setProgressLog(prev => {
        const next = [...prev];
        next[next.length - 1].status = "success";
        return [...next, { msg: "Checking patient details...", status: "loading" }];
      });

      // 3. Process Patient Details
      let existingPatient = null;
      if (aiData.patient) {
        if (aiData.patient.phoneNumber) {
          // Clean phone number of any non-digit chars (spaces, +91, etc) to ensure lookup succeeds
          const cleanPhone = aiData.patient.phoneNumber.replace(/\D/g, '');
          const phoneToSearch = cleanPhone.length > 10 ? cleanPhone.slice(-10) : cleanPhone;

          if (phoneToSearch.length >= 10) {
            try {
              const searchRes = await dispatch(searchCustomerByNumber(phoneToSearch)).unwrap();
              if (searchRes && searchRes.length > 0) {
                existingPatient = searchRes[0];
                setProgressLog(prev => [...prev.slice(0, -1), { msg: `Existing patient found: ${existingPatient.name || cleanPhone}`, status: "success" }]);
              } else {
                setProgressLog(prev => [...prev.slice(0, -1), { msg: "New patient details extracted", status: "success" }]);
              }
            } catch (e) {
              console.error("Lookup failed", e);
            }
          }
        }
      }

      // Give a tiny delay for visual pacing
      await new Promise(r => setTimeout(r, 600));
      setProgressLog(prev => [...prev, { msg: "Setting up the order...", status: "loading" }]);

      // 4. Process Products
      let mappedProducts = [];
      if (aiData.products && aiData.products.length > 0) {
        const idStore = storeID ? storeID : SID;
        // preserve the structure required by PrescriptionMaker
        mappedProducts = aiData.products.map(p => {
          let mrp = "", discount = "", gst = "", netAmt = "", type = "", typeIndex = 0, uom = 0, qty = 0, drugs = [], isCustom = false, productId = null, lookupMarginType = "";
          let productName = p.productName || "";

          if (p.matchedProduct) {
            const dbProduct = p.matchedProduct;
            productName = dbProduct.productName;
            mrp = dbProduct.pricing?.mrp || 0;
            gst = dbProduct.pricing?.gst || 0;
            type = dbProduct.type || "";
            uom = dbProduct.uom || 1;
            productId = dbProduct.IID;
            isCustom = dbProduct.isCustom || (String(productId).startsWith("CP"));
            drugs = dbProduct.drugs || [];

            discount = dbProduct.lookupMargin?.discount || 0;
            lookupMarginType = dbProduct.lookupMargin?.type || "";

            const discountAmt = ((mrp * discount) / 100);
            netAmt = parseFloat((mrp - discountAmt).toFixed(2));

            const storeProduct = dbProduct.storeProducts?.find(storeItem => storeItem.SID === idStore);
            qty = storeProduct?.storeStock || 0;
          }

          const dbProduct = p.matchedProduct || {};
          const metrics = calculateAIProductMetrics(p, dbProduct);

          return {
            productName: productName,
            productId: productId,
            IID: productId || "",
            isCustom: isCustom,
            isAlternate: p.isAlternate || false,
            originalName: p.originalName || "",
            morningTime: Number(p.morningTime) || 0,
            midDay: Number(p.midDay) || 0,
            eveningTime: Number(p.eveningTime) || 0,
            night: Number(p.night) || 0,
            rxDays: metrics.rxDays,
            rxDaysDisplay: p.dosageMode === "NA" ? "NA" : (metrics.rxDays ? String(metrics.rxDays) : ""),
            rxUnits: metrics.rxUnits,
            takenTime: p.takenTime || "A/F",
            dosageNote: p.dosageNote || "",
            orderQty: metrics.orderQty,
            orderUnits: metrics.orderUnits,
            balanceUnits: Math.max(0, metrics.rxUnits - metrics.orderUnits),
            units: uom || 1,
            mrp: mrp, discount: discount, gst: gst, netAmt: netAmt, type: type, typeIndex: typeIndex,
            takenTimeIndex: foodToggleValues.indexOf(p.takenTime) !== -1 ? foodToggleValues.indexOf(p.takenTime) : ((p.takenTime === "B/F" || p.takenTime === "Before Food" || p.takenTime === "Before Breakfast") ? 1 : 0),
            extra: 0, extraIndex: 0, drugs: drugs, qty: qty, uom: uom, lookupMargin: { type: lookupMarginType },
            dosageMode: p.dosageMode || "REGULAR",
            periodicFrequency: p.periodicFrequency || "Once a week",
            reducingStepMode: p.reducingStepMode || "nextDays",
            reducingDoses: Array.isArray(p.reducingDoses) ? p.reducingDoses.map((step, sIdx) => ({
              startDay: Number(step.startDay) || (sIdx === 0 ? 1 : 0),
              endDay: Number(step.endDay) || 0,
              rxDays: Number(step.rxDays) || 0,
              morningTime: Number(step.morningTime) || 0,
              midDay: Number(step.midDay) || 0,
              eveningTime: Number(step.eveningTime) || 0,
              night: Number(step.night) || 0
            })) : []
          };
        });
      }

      // Perform a single atomic state update to prevent Formik validation race conditions
      setValues((prev) => {
        let updatedPatient = {};
        if (existingPatient) {
          updatedPatient = {
            ...prev.patient,
            phoneNumber: String(existingPatient.phoneNumber || ""),
            name: String(existingPatient.name || ""),
            surname: String(existingPatient.surname || ""),
            gender: String(existingPatient.gender || ""),
            dateofbirth: String(existingPatient.dateofbirth || ""),
            age: String(existingPatient.age || ""),
            pincode: String(existingPatient.pincode || existingPatient.patientAddress?.pincode || ""),
            city: String(existingPatient.city || existingPatient.patientAddress?.city || ""),
            PID: String(existingPatient.PID || ""),
          };
        } else if (aiData.patient) {
          updatedPatient = {
            ...prev.patient,
            phoneNumber: String(aiData.patient.phoneNumber || ""),
            name: String(aiData.patient.name || ""),
            surname: String(aiData.patient.surname || prev.patient.surname),
            gender: String(aiData.patient.gender || prev.patient.gender),
            age: String(aiData.patient.age || prev.patient.age),
            dateofbirth: String(aiData.patient.dateofbirth || prev.patient.dateofbirth),
            city: String(aiData.patient.city || prev.patient.city),
            pincode: String(aiData.patient.pincode || prev.patient.pincode),
          };
        } else {
          updatedPatient = prev.patient;
        }

        return {
          ...prev,
          patient: updatedPatient,
          products: mappedProducts.length > 0 ? mappedProducts : prev.products,
          isAIGenerated: true,
        };
      }, true); // `true` forces Formik to run validation immediately on the full fresh state!

      if (setBillingData) {
        setBillingData(0);
      }

      setProgressLog(prev => {
        const next = [...prev];
        next[next.length - 1].status = "success";
        return [...next, { msg: "Order setup complete! Returning to Studio...", status: "success" }];
      });

      // Give user a moment to see completion before closing
      setTimeout(() => {
        closeModal();
        if (onSuccessSwitchTab) {
          onSuccessSwitchTab("create");
        }
      }, 1500);
    } catch (err) {
      toast.error(err.response?.data?.error || err.message || "Failed to process prescription.");
      setProgressLog(prev => [...prev.slice(0, -1), { msg: "Process failed", status: "error" }]);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      processImageBlob(file);
    }
  };

  const handleUploadCaptured = async () => {
    if (imageData) {
      const blob = await (await fetch(imageData)).blob();
      processImageBlob(blob);
    }
  };

  const closeModal = () => {
    setIsOpen(false);
    setImageData(null);
    stopCamera();
    setProgressLog([]);
  };

  // Rotating dynamic text during the longest AI phase
  const [dynamicPhaseText, setDynamicPhaseText] = useState("");
  React.useEffect(() => {
    if (loading && progressLog.find(p => p.msg === "Running intelligence extraction models..." && p.status === "loading")) {
      const phrases = ["Analysing Prescribed Medicines", "Checking Dosages & Frequencies", "Cross-referencing Inventory", "Just a moment"];
      let i = 0;
      setDynamicPhaseText(phrases[0]);
      const interval = setInterval(() => {
        i = (i + 1) % phrases.length;
        setDynamicPhaseText(phrases[i]);
      }, 2500);
      return () => clearInterval(interval);
    } else {
      setDynamicPhaseText("");
    }
  }, [loading, progressLog]);

  return (
    <>
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className="relative overflow-hidden h-[34px] sm:h-[36px] px-3.5 rounded-[9px] flex items-center justify-center gap-2 group border border-indigo-200 hover:border-indigo-400 transition-all shadow-2xs shrink-0 cursor-pointer w-full sm:w-auto"
      >
        {/* Fluid Gradient Background */}
        <div className="absolute inset-0 bg-gradient-to-r from-indigo-50 via-purple-50 to-pink-50 opacity-80 group-hover:opacity-100 transition-opacity bg-[length:200%_200%] animate-gradient-xy"></div>

        <div className="relative z-10 flex items-center gap-1.5">
          <SparklesIcon className="w-4 h-4 text-red-600 animate-pulse shrink-0" />
          <span className="text-[12px] sm:text-[13px] font-bold text-red-900 tracking-wide whitespace-nowrap">
            Rapid Scan Order
          </span>
        </div>
      </button>

      {isOpen && (
        <div className="fixed inset-0 z-[9999] bg-slate-50 flex flex-col overflow-hidden">
          {/* Background Decorative Mesh Orbs */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
            <div className="absolute top-[-10%] left-[-10%] w-[500px] h-[500px] bg-red-200/40 rounded-full mix-blend-multiply filter blur-[100px] opacity-70 animate-blob"></div>
            <div className="absolute top-[20%] right-[-10%] w-[500px] h-[500px] bg-rose-200/40 rounded-full mix-blend-multiply filter blur-[100px] opacity-70 animate-blob animation-delay-2000"></div>
            <div className="absolute bottom-[-20%] left-[20%] w-[500px] h-[500px] bg-pink-200/40 rounded-full mix-blend-multiply filter blur-[100px] opacity-70 animate-blob animation-delay-4000"></div>
          </div>

          {/* Header */}
          <div className="relative z-10 flex items-center justify-between p-4 sm:p-6 border-b border-white/50 bg-white/70 backdrop-blur-xl shadow-sm shrink-0">
            <button
              type="button"
              onClick={closeModal}
              className="flex items-center justify-center w-10 h-10 shrink-0 rounded-full bg-white shadow-sm border border-gray-200 text-gray-500 hover:text-gray-900 hover:scale-105 active:scale-95 transition-all"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
            </button>
            <h3 className="text-[16px] sm:text-xl font-bold text-gray-900 flex items-center justify-center gap-2 sm:gap-3 flex-1 px-2 whitespace-nowrap">
              <SparklesIcon className="w-5 h-5 sm:w-6 sm:h-6 text-red-600 shrink-0" />
              <span className="truncate">Scan Intelligence Engine</span>
            </h3>
            <div className="w-10 h-10 shrink-0"></div> {/* Spacer for centering */}
          </div>

          {/* Main Content Area */}
          <div className="relative z-10 flex-1 overflow-y-auto flex items-center justify-center p-4 sm:p-6">
            {loading || progressLog.length > 0 ? (
              <div className="flex flex-col items-center justify-center w-full max-w-2xl gap-10">
                {/* Glass Logo Loader */}
                <div className="relative flex items-center justify-center w-32 h-32">
                  <svg
                    className="w-24 h-24 overflow-visible"
                    viewBox="0 0 976 1024"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <defs>
                      <filter id="glass-3d" x="-50%" y="-50%" width="200%" height="200%">
                        {/* 1. Self-radiance glowing aura */}
                        <feDropShadow dx="0" dy="0" stdDeviation="25" floodColor="#ef4444" floodOpacity="0.8" result="glow" />
                        <feDropShadow dx="0" dy="10" stdDeviation="15" floodColor="#9f1239" floodOpacity="0.5" result="shadow" />

                        {/* 2. Base translucent frosty fill (more vivid red) */}
                        <feComponentTransfer in="SourceAlpha" result="alpha-base">
                          <feFuncA type="linear" slope="0.6" />
                        </feComponentTransfer>
                        <feFlood floodColor="#ef4444" result="color-red" />
                        <feComposite in="color-red" in2="alpha-base" operator="in" result="glass-base" />

                        {/* 3. Internal depth/shadow (curved edges) */}
                        <feOffset dx="6" dy="6" in="SourceAlpha" result="inner-shadow-offset" />
                        <feGaussianBlur stdDeviation="8" in="inner-shadow-offset" result="inner-shadow-blur" />
                        <feComposite operator="out" in="SourceAlpha" in2="inner-shadow-blur" result="inner-shadow-mask" />
                        <feFlood floodColor="#7f1d1d" floodOpacity="0.7" result="inner-shadow-color" />
                        <feComposite operator="in" in="inner-shadow-color" in2="inner-shadow-mask" result="inner-shadow" />

                        {/* 4. Reflective Specular Highlights (light reflecting off glass edges) */}
                        <feGaussianBlur in="SourceAlpha" stdDeviation="4" result="edge-blur" />
                        <feSpecularLighting in="edge-blur" surfaceScale="6" specularConstant="1.5" specularExponent="40" lightingColor="white" result="specular">
                          {/* Fixed light source at top-left relative to the stationary SVG */}
                          <fePointLight x="-100" y="-100" z="200" />
                        </feSpecularLighting>
                        <feComposite in="specular" in2="SourceAlpha" operator="in" result="edge-highlight" />

                        {/* 5. Secondary highlight from bottom right */}
                        <feSpecularLighting in="edge-blur" surfaceScale="4" specularConstant="0.8" specularExponent="20" lightingColor="#fecdd3" result="specular-alt">
                          <fePointLight x="1000" y="1000" z="150" />
                        </feSpecularLighting>
                        <feComposite in="specular-alt" in2="SourceAlpha" operator="in" result="edge-highlight-alt" />

                        <feMerge>
                          <feMergeNode in="glow" />
                          <feMergeNode in="shadow" />
                          <feMergeNode in="glass-base" />
                          <feMergeNode in="inner-shadow" />
                          <feMergeNode in="edge-highlight" />
                          <feMergeNode in="edge-highlight-alt" />
                        </feMerge>
                      </filter>
                    </defs>
                    {/* Apply filter to stationary group so light reflections are dynamic */}
                    <g filter="url(#glass-3d)">
                      {/* Zoom animation */}
                      <g className="animate-logo-zoom" style={{ transformOrigin: '488px 512px' }}>
                        {/* Spin animation */}
                        <g className="animate-[spin_4s_linear_infinite]" style={{ transformOrigin: '488px 512px' }}>
                          <path
                            d="M444.483 112.385C462.71 98.1432 484.279 91.0225 509.188 91.0225C528.023 91.0225 544.579 95.2023 558.856 103.562C573.438 111.921 584.829 123.53 593.031 138.391C601.233 153.251 605.335 170.435 605.335 189.939V308.771L858.666 162.513L909.651 218.313L812.322 274.506C833.769 283.17 850.721 298.288 863.176 319.86C872.593 336.171 877.251 352.598 877.15 369.143C877.202 385.95 872.843 401.621 864.074 416.154C855.305 430.688 842.475 442.831 825.583 452.583L722.67 511.999L976 658.259L953.167 730.314L855.839 674.122C859.059 697.027 854.441 719.266 841.986 740.838C832.569 757.149 820.672 769.397 806.294 777.582C791.764 786.03 776.013 790.091 759.042 789.764C742.071 789.436 725.14 784.396 708.248 774.644L605.335 715.227V1007.75L531.517 1024V911.615C513.29 925.857 491.721 932.978 466.812 932.978C447.977 932.978 431.421 928.799 417.144 920.439C402.562 912.08 391.171 900.47 382.969 885.609C374.767 870.749 370.666 853.565 370.666 834.061V715.226L117.334 861.486L66.3486 805.686L163.678 749.493C142.231 740.829 125.28 725.711 112.825 704.139C103.408 687.828 98.7491 671.401 98.8496 654.856C98.7983 638.049 103.157 622.378 111.926 607.845C120.695 593.311 133.525 581.168 150.417 571.416L253.329 511.999L0 365.74L22.833 293.686L120.161 349.878C116.941 326.973 121.559 304.733 134.014 283.161C143.431 266.85 155.328 254.602 169.706 246.417C184.236 237.969 199.987 233.908 216.958 234.235C233.929 234.563 250.86 239.603 267.752 249.355L370.666 308.772V16.2539L444.483 0V112.385Z"
                            fill="white"
                          />
                        </g>
                      </g>
                    </g>
                  </svg>
                </div>

                {/* Dynamic Progress Log */}
                <div className="w-full max-w-md bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-6 flex flex-col gap-4">
                  {progressLog.map((log, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <div className="mt-1">
                        {log.status === "success" && (
                          <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center">
                            <svg className="w-3 h-3 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                          </div>
                        )}
                        {log.status === "loading" && (
                          <div className="w-5 h-5 rounded-full border-2 border-red-200 border-t-red-600 animate-spin"></div>
                        )}
                        {log.status === "error" && (
                          <div className="w-5 h-5 rounded-full bg-red-100 flex items-center justify-center">
                            <svg className="w-3 h-3 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col">
                        <span className={`text-[15px] font-medium ${log.status === 'success' ? 'text-gray-800' : log.status === 'error' ? 'text-red-600' : 'text-red-600'}`}>{log.msg}</span>
                        {log.status === "loading" && dynamicPhaseText && log.msg === "Running intelligence extraction models..." && (
                          <span className="text-[13px] text-gray-500 mt-1 animate-pulse">{dynamicPhaseText}...</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="flex flex-col gap-6 sm:gap-8 w-full max-w-2xl bg-white/80 backdrop-blur-2xl p-6 sm:p-10 rounded-2xl sm:rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-white/60 relative overflow-hidden">
                {/* Glass edge reflection */}
                <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-white to-transparent opacity-80"></div>

                <div className="flex items-center justify-center mb-[-10px]">
                  <div className="w-16 h-16 bg-red-50 rounded-2xl flex items-center justify-center shadow-inner border border-red-100/50">
                    <SparklesIcon className="w-8 h-8 text-red-500" />
                  </div>
                </div>

                <p className="text-[14px] sm:text-[15px] text-gray-600 text-center mb-1 sm:mb-2 leading-relaxed font-medium">
                  Upload a clear photo of the prescription. Our intelligence engine will automatically extract patient details and medications for you.
                </p>

                {!cameraOn && !imageData && (
                  <div className="grid grid-cols-2 gap-4 sm:gap-5">
                    <Button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="group flex flex-col items-center justify-center gap-3 sm:gap-4 bg-gradient-to-b from-white to-red-50/30 hover:to-red-50/80 border border-red-100 hover:border-red-300 hover:shadow-md text-red-700 rounded-[20px] p-4 sm:p-6 transition-all duration-300 h-[120px] sm:h-[140px] relative overflow-hidden"
                    >
                      <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                        <UploadFile width={24} height={24} fill="currentColor" className="sm:w-6 sm:h-6" />
                      </div>
                      <span className="text-[14px] sm:text-[15px] font-bold">Upload Photo</span>
                    </Button>
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileChange}
                      className="hidden"
                      accept="image/*,application/pdf"
                    />

                    <Button
                      type="button"
                      onClick={startCamera}
                      className="group flex flex-col items-center justify-center gap-3 sm:gap-4 bg-gradient-to-b from-white to-rose-50/30 hover:to-rose-50/80 border border-rose-100 hover:border-rose-300 hover:shadow-md text-rose-700 rounded-[20px] p-4 sm:p-6 transition-all duration-300 h-[120px] sm:h-[140px] relative overflow-hidden"
                    >
                      <div className="w-12 h-12 rounded-full bg-rose-50 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                        <Camera width={24} height={24} fill="currentColor" className="sm:w-6 sm:h-6" />
                      </div>
                      <span className="text-[14px] sm:text-[15px] font-bold">Open Camera</span>
                    </Button>
                  </div>
                )}

                {cameraOn && (
                  <div className="flex flex-col gap-4">
                    <div className="rounded-[16px] overflow-hidden bg-black aspect-[3/4] relative">
                      <video ref={videoRef} autoPlay playsInline className="w-full h-full object-cover" />
                    </div>
                    <div className="flex gap-3">
                      <Button type="button" onClick={stopCamera} className="flex-1 bg-gray-100 text-gray-700 border-gray-200 py-3 rounded-xl text-[14px] font-medium">Cancel Camera</Button>
                      <Button type="button" onClick={capturePhoto} className="flex-1 bg-red-600 text-white hover:bg-red-700 py-3 rounded-xl text-[14px] font-medium shadow-md shadow-red-200">Capture</Button>
                    </div>
                    <canvas ref={canvasRef} className="hidden" />
                  </div>
                )}

                {imageData && (
                  <div className="flex flex-col gap-4">
                    <div className="rounded-[16px] overflow-hidden border border-gray-200 relative aspect-[3/4] bg-gray-50 flex items-center justify-center">
                      <img src={imageData} alt="Captured" className="max-w-full max-h-full object-contain" />
                    </div>
                    <div className="flex gap-3">
                      <Button type="button" onClick={() => { setImageData(null); startCamera(); }} className="flex-1 bg-gray-100 text-gray-700 border-gray-200 py-3 rounded-xl text-[14px] font-medium">Retake</Button>
                      <Button type="button" onClick={handleUploadCaptured} className="flex-1 bg-red-600 text-white hover:bg-red-700 py-3 rounded-xl text-[14px] font-medium shadow-md shadow-red-200">Process with Engine</Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="relative z-10 p-4 sm:p-6 bg-white/70 backdrop-blur-xl border-t border-white/50 flex flex-col items-center justify-center shrink-0 shadow-[0_-4px_20px_rgb(0,0,0,0.02)]">
            <p className="text-[11px] sm:text-[12px] text-gray-500 text-center mb-4 sm:mb-5 max-w-2xl leading-relaxed">
              <span className="font-semibold text-gray-700">Intelligence Disclaimer:</span> Our intelligence engine automatically extracts details from prescriptions, but it can make mistakes. You must thoroughly verify all generated patient and medication details in the Studio before finalizing the order.
            </p>
            <Button
              type="button"
              onClick={closeModal}
              className="w-full sm:w-auto min-w-[200px] bg-white text-gray-700 hover:bg-gray-50 border border-gray-200 hover:border-gray-300 py-3.5 sm:py-3.5 rounded-xl font-bold shadow-sm transition-all text-[15px]"
            >
              Return to Studio
            </Button>
          </div>
        </div>
      )}
    </>
  );
};

export default AIPrescriptionReader;
